import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import {
  Container,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import vehicleService from '../../services/vehicleService';
import { organizationService } from '../../services/organizationService';
import { Vehicle } from '../../types/vehicle';
import CameraModal from '../../components/camera/CameraModal';
import { useMileageAI } from '../../hooks/useMileageAI';

const QuickMileageEntryPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [mileage, setMileage] = useState<number>(0);
  const [showMileageInput, setShowMileageInput] = useState(false);

  const {
    analyzing,
    aiMileage,
    aiConfidence,
    validationResult,
    error: aiError,
    analyzeImage,
    validateMileage,
    reset: resetAI
  } = useMileageAI();

  useEffect(() => {
    const loadVehicles = async () => {
      try {
        if (!user?.organizationId || !user?.id) return;

        // Check if user is Admin
        const adminStatus = await organizationService.isUserAdmin(user.organizationId, user.id);
        setIsAdmin(adminStatus);

        if (adminStatus) {
          // Admin: load all organization vehicles
          const { vehicles: vehiclesList } = await vehicleService.getOrganizationVehicles(user.organizationId);
          setVehicles(vehiclesList);
        } else {
          // User: load only assigned vehicle
          const assignedVehicle = await vehicleService.getAssignedVehicle(user.organizationId, user.id);

          if (assignedVehicle) {
            setVehicles([assignedVehicle]);
            setSelectedVehicle(assignedVehicle.id);
          } else {
            setError(t('vehicle.errors.noVehicleAssigned'));
          }
        }
      } catch (err) {
        console.error('Error loading vehicles:', err);
        setError(t('vehicle.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadVehicles();
  }, [user, t]);

  useEffect(() => {
    if (aiMileage && selectedVehicle) {
      const vehicle = vehicles.find(v => v.id === selectedVehicle);
      if (vehicle) {
        validateMileage(aiMileage, vehicle.currentMileage || 0);
      }
    }
  }, [aiMileage, selectedVehicle, vehicles, validateMileage]);

  const handleVehicleSelect = (event: SelectChangeEvent<string>) => {
    setSelectedVehicle(event.target.value);
    resetAI();
    setSelectedImage(null);
    setPreviewUrl(null);
    setShowMileageInput(false);
  };

  const handlePhotoTaken = async (file: File) => {
    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
    await analyzeImage(file);
  };

  const handleSubmit = async () => {
    try {
      if (!user || !selectedVehicle || isSubmitting) return;

      setIsSubmitting(true);
      setError(null);

      const vehicle = vehicles.find(v => v.id === selectedVehicle);
      if (!vehicle) return;

      if (mileage <= (vehicle.currentMileage || 0)) {
        setError(t('vehicle.mileage.errors.invalidMileage'));
        setIsSubmitting(false);
        return;
      }

      let imageUrl = null;
      if (selectedImage) {
        const document = await vehicleService.uploadDocument(
          selectedVehicle,
          vehicle.organizationId,
          selectedImage,
          'maintenance'
        );
        imageUrl = document.url;
      }

      // Update vehicle's current mileage first
      await vehicleService.updateVehicle(selectedVehicle, {
        currentMileage: mileage,
        lastMileageUpdate: Timestamp.now()
      });

      // Then add the mileage report
      const reportData: any = {
        mileage,
        date: Timestamp.now(),
        reportedBy: user.id,
        aiVerified: !!aiMileage,
        validationStatus: validationResult?.isValid ? 'validated' : 'pending',
      };

      // Only add optional fields if they have values
      if (imageUrl) reportData.imageUrl = imageUrl;
      if (aiConfidence !== null) reportData.aiConfidence = aiConfidence;
      if (aiMileage !== null) reportData.originalAiReading = aiMileage;
      if (validationResult?.reason) reportData.validationMessage = validationResult.reason;

      await vehicleService.addMileageReport(selectedVehicle, reportData);

      // Reset form
      setSelectedVehicle('');
      setSelectedImage(null);
      setPreviewUrl(null);
      setShowMileageInput(false);
      resetAI();
      setError(null);

      // Show success message
      alert(t('vehicle.mileage.messages.success'));
    } catch (err) {
      console.error('Error submitting mileage:', err);
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box py={2}>
        <Typography variant="h5" gutterBottom align="center">
          {t('vehicle.mileage.quickEntry')}
        </Typography>

        <Paper sx={{ p: 2, mt: 2 }}>
          {isAdmin ? (
            // Admin: Show vehicle selector
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('vehicle.select')}</InputLabel>
              <Select
                value={selectedVehicle}
                onChange={handleVehicleSelect}
                label={t('vehicle.select')}
                disabled={isSubmitting}
              >
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.make} {vehicle.model} - {vehicle.licensePlate}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            // User: Show assigned vehicle info
            vehicles.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: theme.palette.primary.light,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {t('vehicle.assignment.assignedVehicle')}
                </Typography>
                <Typography variant="h6">
                  {vehicles[0].make} {vehicles[0].model}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vehicles[0].licensePlate}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('vehicle.mileage.current')}: {vehicles[0].currentMileage || 0} km
                </Typography>
              </Box>
            )
          )}

          {selectedVehicle && (
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setShowCamera(true)}
                disabled={analyzing || isSubmitting}
              >
                {t('vehicle.mileage.takePhoto')}
              </Button>

              <CameraModal
                open={showCamera}
                onClose={() => setShowCamera(false)}
                onPhotoTaken={handlePhotoTaken}
              />

              {previewUrl && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('vehicle.details.buttons.viewDashboardPhoto')}:
                  </Typography>
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Dashboard preview"
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}

              {analyzing && (
                <Box display="flex" alignItems="center" gap={1} my={2}>
                  <CircularProgress size={20} />
                  <Typography>{t('vehicle.mileage.ai.analyzing')}</Typography>
                </Box>
              )}

              {aiMileage !== null && !showMileageInput && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('vehicle.mileage.ai.detected')}: {aiMileage}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => {
                        setMileage(aiMileage);
                        setShowMileageInput(true);
                      }}
                    >
                      {t('vehicle.mileage.ai.confirm')}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setShowMileageInput(true)}
                    >
                      {t('vehicle.mileage.ai.edit')}
                    </Button>
                  </Box>
                </Box>
              )}

              {showMileageInput && (
                <Box mt={2}>
                  {selectedVehicle && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      gutterBottom
                    >
                      {t('vehicle.mileage.current')}: {
                        vehicles.find(v => v.id === selectedVehicle)?.currentMileage || 0
                      } km
                    </Typography>
                  )}
                  
                  <TextField
                    fullWidth
                    type="number"
                    label={t('vehicle.mileage.enter')}
                    value={mileage}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value)) {
                        setMileage(value);
                      }
                    }}
                    disabled={isSubmitting}
                    InputProps={{
                      inputProps: {
                        min: vehicles.find(v => v.id === selectedVehicle)?.currentMileage || 0
                      },
                      endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>km</Typography>
                    }}
                    helperText={t('vehicle.mileage.mustBeHigher')}
                    error={mileage <= (vehicles.find(v => v.id === selectedVehicle)?.currentMileage || 0)}
                  />

                  {aiMileage !== null && mileage !== aiMileage && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {t('vehicle.mileage.ai.originalValue')}: {aiMileage}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleSubmit}
                    disabled={mileage <= 0 || isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isSubmitting ? t('common.saving') : t('common.save')}
                  </Button>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {aiError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {aiError}
                </Alert>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default QuickMileageEntryPage;
